import { useMemo } from 'react';
import { Control, FieldErrors, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { AddStandardFormData } from '@/schema/template';

interface BilingualTextAreasProps {
  control: Control<AddStandardFormData>;
  errors: FieldErrors<AddStandardFormData>;
}

export function BilingualTextAreas({
  control,
  errors,
}: BilingualTextAreasProps) {
  const { t, i18n } = useTranslation();
  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);

  return (
    <div
      className={cn(
        'grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 md:gap-6',
        isRTL && 'rtl'
      )}
    >
      <div className="space-y-1 sm:space-y-2">
        <Label
          htmlFor="standardTextEnglish"
          className={cn('text-xs sm:text-sm', isRTL && 'text-right')}
        >
          <span className="hidden sm:inline">
            {t('templates.admin.addStandards.form.standardTextEnglish')}{' '}
            {t('templates.admin.addStandards.form.required')}
          </span>
          <span className="sm:hidden">English Text *</span>
        </Label>
        <Controller
          name="standardTextEnglish"
          control={control}
          render={({ field }) => (
            <Textarea
              {...field}
              placeholder={
                isRTL
                  ? 'النص الإنجليزي للمعيار'
                  : 'Enter standard text in English'
              }
              rows={3}
              className={cn(
                'text-xs sm:text-sm resize-none',
                errors.standardTextEnglish && 'border-red-500'
              )}
              dir="ltr"
            />
          )}
        />
        {errors.standardTextEnglish && (
          <p className={cn('text-xs text-red-500', isRTL && 'text-right')}>
            {errors.standardTextEnglish.message}
          </p>
        )}
      </div>

      <div className="space-y-1 sm:space-y-2">
        <Label
          htmlFor="standardTextArabic"
          className={cn('text-xs sm:text-sm', isRTL && 'text-right')}
        >
          <span className="hidden sm:inline">
            {t('templates.admin.addStandards.form.standardTextArabic')}{' '}
            {t('templates.admin.addStandards.form.required')}
          </span>
          <span className="sm:hidden">Arabic Text *</span>
        </Label>
        <Controller
          name="standardTextArabic"
          control={control}
          render={({ field }) => (
            <Textarea
              {...field}
              placeholder={
                isRTL
                  ? 'ادخل نص المعيار باللغة العربية'
                  : 'Enter standard text in Arabic'
              }
              rows={3}
              className={cn(
                'text-right text-xs sm:text-sm resize-none',
                errors.standardTextArabic && 'border-red-500'
              )}
              dir="rtl"
            />
          )}
        />
        {errors.standardTextArabic && (
          <p className={cn('text-xs text-red-500', isRTL && 'text-right')}>
            {errors.standardTextArabic.message}
          </p>
        )}
      </div>
    </div>
  );
}
